import React, { ReactNode, useCallback, useRef, useState } from "react";
import styles from "./index.module.scss";
import ShowEyeIcon from "@Assets/icons/show-eye.svg";
import CloseEyeIcon from "@Assets/icons/close-eye.svg";
import CloseIcon from "@Assets/icons/close.svg";
import SearchIcon from "@Assets/icons/search-icon.svg";
import InputLabel from "./InputLabel";

export type TextInputProps = {
  placeholder?: string;
  disabled?: boolean;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  name?: string;
  isPassword?: boolean;
  rightIcon?: ReactNode;
  leftIcon?: ReactNode;
  className?: string;
  label: string;
  maxLength?: number;
  type?: string;
  classes?: string;
  showClearIcon?: boolean;
  isSearch?: boolean;
  spellCheck?: boolean;
};

const Input: React.FC<TextInputProps> = ({
  placeholder = "Enter Placeholder Text",
  label,
  value,
  disabled,
  onChange = () => {},
  onBlur,
  name,
  isPassword = false,
  rightIcon,
  leftIcon,
  maxLength,
  classes = "",
  type = "text",
  showClearIcon = false,
  isSearch = false,
  ...rest
}) => {
  const [focus, setFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleClick = () => inputRef.current?.focus();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      // debug: log input name and value
      // eslint-disable-next-line no-console
      console.log("TextInput change:", name, e.target.value);
    } catch {}
    onChange(e);
  };

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prevState) => !prevState);
  }, []);

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (inputRef.current) {
        const input = inputRef.current;
        const nativeValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value"
        )?.set;

        if (nativeValueSetter) {
          nativeValueSetter.call(input, "");
          const nativeEvent = new Event("input", {
            bubbles: true,
            cancelable: true,
          });
          input.dispatchEvent(nativeEvent);
        }

        const syntheticTarget = {
          ...input,
          value: "",
        };

        Object.defineProperty(syntheticTarget, "value", {
          value: "",
          writable: true,
          enumerable: true,
          configurable: true,
        });

        const syntheticEvent = {
          target: syntheticTarget as HTMLInputElement,
          currentTarget: input,
        } as React.ChangeEvent<HTMLInputElement>;

        onChange(syntheticEvent);
      }
    },
    [onChange]
  );

  return (
    <div className="flex flex-col w-full">
      {label && <InputLabel label={label} />}
      <div
        className={`${styles.container} ${
          focus ? styles["active-input"] : ""
        } ${isSearch && focus ? styles["active-search-input"] : ""} ${classes ?? ""}`}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      >
        {(isSearch || leftIcon) && (
          <div className={styles["left-icon"]}>
            {isSearch ? <SearchIcon /> : leftIcon}
          </div>
        )}

        <div className="flex flex-col flex-grow mr-4 max-w-full ">
          <input
            disabled={disabled}
            onChange={handleChange}
            onFocus={() => {
              try {
                // eslint-disable-next-line no-console
                console.log("TextInput focus:", name);
              } catch {}
            }}
            onMouseDown={() => {
              try {
                // eslint-disable-next-line no-console
                console.log("TextInput mousedown:", name);
              } catch {}
            }}
            placeholder={placeholder}
            value={value ?? ""}
            className={`${styles["custom-input"]} ${
              isSearch ? styles["search-input"] : ""
            }`}
            style={{ pointerEvents: "auto" }}
            ref={inputRef}
            onBlur={onBlur}
            name={name}
            type={isPassword ? (showPassword ? "text" : "password") : type}
            maxLength={maxLength}
            pattern={type === "tel" ? "^(+d{1,3})?d{10,15}$" : undefined}
            {...rest}
          />
        </div>

        {isPassword && (
          <div
            onClick={togglePasswordVisibility}
            className="flex items-center cursor-pointer"
          >
            {showPassword ? (
              <span className="w-5 h-5 inline-flex items-center justify-center">
                <ShowEyeIcon />
              </span>
            ) : (
              <span className="w-5 h-5 inline-flex items-center justify-center">
                <CloseEyeIcon />
              </span>
            )}
          </div>
        )}

        {showClearIcon && value && !isPassword && (
          <div
            onClick={handleClear}
            className={`${styles["clear-icon"]} cursor-pointer ml-2`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClear(e as any);
              }
            }}
            aria-label="Clear input"
          >
            <CloseIcon />
          </div>
        )}

        {rightIcon && rightIcon}
      </div>
    </div>
  );
};

export default Input;
